import { Payslip, Council } from '@/types';
import { formatCurrency, formatDate } from '@/lib/utils';
import { PayslipDocumentHeader } from './PayslipDocumentHeader';

interface PayslipDocumentProps {
  payslip: Payslip;
  employeeName: string;
  manNumber?: string;
  departmentName?: string;
  positionName?: string;
  council?: Council | null;
}

export const PayslipDocument = ({
  payslip,
  employeeName,
  manNumber,
  departmentName,
  positionName,
  council,
}: PayslipDocumentProps) => {
  const allowances = payslip.allowanceBreakdown || [];
  const deductions = payslip.deductionBreakdown || [];
  const grossSalary = payslip.grossSalary || (payslip.basicSalary + payslip.totalAllowances);
  const displayManNumber = manNumber || payslip.manNumber || 'N/A';

  return (
    <div
      className="bg-white p-3 sm:p-5 max-w-[210mm] mx-auto text-xs leading-normal payslip-print-root w-full overflow-x-hidden"
      id="payslip-document"
    >
      <PayslipDocumentHeader
        council={council}
        payPeriod={payslip.period || payslip.payPeriod}
        issueDate={payslip.issueDate}
        compact
      />

      <div className="bg-gray-50 p-3 sm:p-4 mb-3 rounded">
        <h3 className="text-sm font-semibold mb-2 text-gray-900">Employee Information</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 sm:gap-x-4 gap-y-2">
          <div className="min-w-0">
            <p className="text-[10px] text-gray-600">Employee Name</p>
            <p className="font-medium break-words">{employeeName}</p>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-gray-600">Man Number</p>
            <p className="font-medium break-words">{displayManNumber}</p>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-gray-600">Department</p>
            <p className="font-medium break-words">{departmentName || payslip.department || 'N/A'}</p>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-gray-600">Position</p>
            <p className="font-medium break-words">{positionName || payslip.position || 'N/A'}</p>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-gray-600">Bank Account</p>
            <p className="font-medium break-words">{payslip.bankAccount || 'N/A'}</p>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-gray-600">Bank Name</p>
            <p className="font-medium break-words">{payslip.bankName || 'N/A'}</p>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-gray-600">NRC</p>
            <p className="font-medium break-words">{payslip.nrc || 'N/A'}</p>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-gray-600">TPIN</p>
            <p className="font-medium break-words">{payslip.taxId || 'N/A'}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 mb-3">
        <div className="bg-green-50 p-3 sm:p-4 rounded">
          <h3 className="text-sm font-semibold mb-2 text-green-800">Earnings</h3>
          <div className="space-y-2">
            <div className="flex justify-between items-start gap-3 py-1 px-1">
              <span className="text-gray-700">Basic Salary</span>
              <span className="font-medium shrink-0">{formatCurrency(payslip.basicSalary)}</span>
            </div>
            {allowances.length > 0 ? (
              allowances.map((allowance, index) => (
                <div key={index} className="flex justify-between items-start gap-3 py-1.5 px-2">
                  <span className="flex-1 min-w-0 text-gray-600 break-words leading-snug pr-2">
                    {allowance.name}
                  </span>
                  <span className="font-medium shrink-0 whitespace-nowrap">
                    {formatCurrency(allowance.amount)}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-gray-500 italic ml-2 px-2">No allowances</div>
            )}
            <div className="border-t pt-2 mt-1 flex justify-between font-semibold text-green-800 px-1 gap-2">
              <span>Total Earnings</span>
              <span className="shrink-0">{formatCurrency(grossSalary)}</span>
            </div>
          </div>
        </div>

        <div className="bg-red-50 p-3 sm:p-4 rounded">
          <h3 className="text-sm font-semibold mb-2 text-red-800">Deductions</h3>
          <div className="space-y-2">
            {deductions.length > 0 ? (
              deductions.map((deduction, index) => (
                <div key={index} className="flex justify-between items-start gap-3 py-1.5 px-2">
                  <span className="flex-1 min-w-0 text-gray-600 break-words leading-snug pr-2">
                    {deduction.name}
                  </span>
                  <span className="font-medium text-red-600 shrink-0 whitespace-nowrap">
                    -{formatCurrency(deduction.amount)}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-gray-500 italic ml-2 px-2">No deductions</div>
            )}
            <div className="border-t pt-2 mt-1 flex justify-between font-semibold text-red-800 px-1 gap-2">
              <span>Total Deductions</span>
              <span className="shrink-0">-{formatCurrency(payslip.totalDeductions)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 p-3 sm:p-4 rounded mb-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h3 className="text-sm font-bold text-blue-900">Net Salary</h3>
            <p className="text-[10px] text-blue-700">Amount to be paid</p>
          </div>
          <div className="sm:text-right">
            <div className="text-base sm:text-lg font-bold text-blue-900">{formatCurrency(payslip.netSalary)}</div>
            <p className="text-[10px] text-blue-700">
              {payslip.paymentMethod || 'Bank Transfer'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 mb-3">
        <div className="bg-gray-50 p-2.5 sm:p-3 rounded">
          <h4 className="font-semibold text-gray-900 mb-1 text-[10px]">Leave &amp; Advances</h4>
          <div className="space-y-0.5">
            {/* <div className="flex justify-between gap-1">
              <span className="text-gray-600">Leave Opening:</span>
              <span className="font-medium">
                {payslip.openingLeaveDays != null
                  ? `${Number(payslip.openingLeaveDays).toFixed(1)} d`
                  : 'N/A'}
              </span>
            </div> */}
            <div className="flex justify-between gap-1">
              <span className="text-gray-600">Leave Balance:</span>
              <span className="font-medium">
                {payslip.closingLeaveDays != null
                  ? `${Number(payslip.closingLeaveDays).toFixed(1)} d`
                  : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between gap-1">
              <span className="text-gray-600">Advance B/F:</span>
              <span>{formatCurrency(payslip.advanceBF ?? 0)}</span>
            </div>
            <div className="flex justify-between gap-1">
              <span className="text-gray-600">Advance C/F:</span>
              <span>{formatCurrency(payslip.advanceCF ?? 0)}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-2.5 sm:p-3 rounded">
          <h4 className="font-semibold text-gray-900 mb-1 text-[10px]">Period</h4>
          <div className="space-y-0.5">
            <div className="flex justify-between gap-1">
              <span className="text-gray-600">Pay Period:</span>
              <span>{payslip.period || payslip.payPeriod || 'N/A'}</span>
            </div>
            <div className="flex justify-between gap-1">
              <span className="text-gray-600">Issue Date:</span>
              <span>{payslip.issueDate ? formatDate(payslip.issueDate) : 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-2.5 sm:p-3 rounded sm:col-span-2">
          <h4 className="font-semibold text-gray-900 mb-1 text-[10px]">Contact</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-2 gap-y-1">
            <div className="flex justify-between gap-1 min-w-0">
              <span className="text-gray-600 shrink-0">Phone:</span>
              <span className="truncate">{payslip.phone || 'N/A'}</span>
            </div>
            <div className="flex justify-between gap-1 min-w-0">
              <span className="text-gray-600 shrink-0">Email:</span>
              <span className="break-all text-right">{payslip.employeeEmail || 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t pt-2 text-center text-[10px] text-gray-500">
        <p>Computer-generated payslip. No signature required.</p>
        {payslip.notes && (
          <p className="mt-1 text-yellow-700">{payslip.notes}</p>
        )}
      </div>
    </div>
  );
};
